import React, { useState, useEffect, useCallback } from 'react';
import axios from '../api/axios';
import { formatCurrency } from '../utils/currency';
import { toast } from '../components/Toast';
import './FinancialTransactions.css';
import './CashAdvance.css';

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

function CashAdvance() {
  const [tab, setTab]                     = useState('give');   // 'give' | 'settle' | 'history'
  const [employees, setEmployees]         = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [advances, setAdvances]           = useState([]);
  const [settlements, setSettlements]     = useState([]);
  const [settlementMap, setSettlementMap] = useState({});   // { advId: totalSpent }
  const [returnMap, setReturnMap]         = useState({});   // { advId: totalReturned }
  const [loading, setLoading]             = useState(false);
  const [histLoading, setHistLoading]     = useState(false);
  const [message, setMessage]             = useState({ type: '', text: '' });
  const [historyTxn, setHistoryTxn] = useState(null);
  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (historyTxn) {
      fetchHistory(historyTxn.id);
    }
  }, [historyTxn]);

  const fetchHistory = async (txnId) => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`/financial-transactions/${txnId}/approval-history`);
      setHistoryList(res.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const renderStatus = (txn) => {
    const status = txn.status || 'APPROVED';
    let bg = '#e2e8f0', color = '#475569', label = status;
    if (status === 'APPROVED') { bg = '#dcfce7'; color = '#15803d'; label = 'Approved'; }
    else if (status === 'REJECTED') { bg = '#fee2e2'; color = '#b91c1c'; label = 'Rejected'; }
    else if (status === 'PENDING_L1') { bg = '#fef3c7'; color = '#d97706'; label = 'Pending L1'; }
    else if (status === 'PENDING_L2') { bg = '#f3e8ff'; color = '#7e22ce'; label = 'Pending L2'; }
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
        <span 
          style={{ 
            backgroundColor: bg, 
            color: color, 
            padding: '2px 8px', 
            borderRadius: '4px', 
            fontSize: '11px', 
            fontWeight: '600',
            textTransform: 'uppercase',
            display: 'inline-block'
          }}
        >
          {label}
        </span>
        <button
          type="button"
          onClick={() => setHistoryTxn(txn)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '14px' }}
          title="View Approval History"
        >
          ℹ️
        </button>
      </div>
    );
  };

  const renderHistoryModal = () => {
    if (!historyTxn) return null;
    return (
      <div className="modal-overlay" style={{ zIndex: 1200, display: 'flex', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }} onClick={() => setHistoryTxn(null)}>
        <div className="modal-content" style={{ maxWidth: '500px', background: 'white', borderRadius: '8px', width: '90%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} onClick={e => e.stopPropagation()}>
          <div className="modal-header" style={{ padding: '20px', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold' }}>Approval History - {historyTxn.voucherNo}</h2>
            <button type="button" className="close-button" onClick={() => setHistoryTxn(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
          </div>
          <div className="modal-body" style={{ padding: '20px', maxHeight: '400px', overflowY: 'auto' }}>
            {historyLoading ? (
              <div>Loading history...</div>
            ) : historyList.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>No history logs found (Bypassed or legacy transaction).</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {historyList.map((h, i) => {
                  let actColor = 'var(--text-main)';
                  if (h.action?.includes('APPROVE')) actColor = 'green';
                  if (h.action?.includes('REJECT')) actColor = 'red';
                  return (
                    <div key={h.id} style={{ display: 'flex', gap: '12px', borderBottom: i < historyList.length - 1 ? '1px solid #eee' : 'none', paddingBottom: '10px' }}>
                      <div style={{ fontSize: '18px' }}>
                        {h.action?.includes('APPROVE') ? '✅' : h.action?.includes('REJECT') ? '❌' : '📤'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: '600', color: actColor }}>{h.action}</span>
                          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{new Date(h.performedAt).toLocaleString()}</span>
                        </div>
                        <div style={{ fontSize: '13px', marginTop: '2px' }}>
                          By: <strong>{h.performedByName}</strong> ({h.performedByRole})
                        </div>
                        {h.remarks && (
                          <div style={{ fontSize: '13px', background: '#f8f9fa', padding: '6px', borderRadius: '4px', marginTop: '4px', color: '#555', fontStyle: 'italic' }}>
                            Remarks: "{h.remarks}"
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={() => setHistoryTxn(null)}>Close</button>
          </div>
        </div>
      </div>
    );
  };

  // Give cash form
  const [giveForm, setGiveForm] = useState({
    employeeId: '',
    amount: '',
    paymentAccountHeadId: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  // Settle form
  const [settleForm, setSettleForm] = useState({
    advanceId: '',
    settledAmount: '',
    returnedAmount: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const [selectedAdvance, setSelectedAdvance] = useState(null);

  useEffect(() => {
    fetchEmployees();
    fetchPaymentAccounts();
    fetchAdvances();
  }, []);

  const fetchEmployees = async () => {
    try {
      const res = await axios.get('/employees?size=1000');
      const data = res.data?.employees || res.data?.data?.employees || res.data?.data || [];
      setEmployees(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  const fetchPaymentAccounts = async () => {
    try {
      const res = await axios.get('/account-heads/category/PAYMENT_ACCOUNT');
      const data = res.data.data || res.data || [];
      setPaymentAccounts(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  const fetchAdvances = useCallback(async () => {
    setHistLoading(true);
    try {
      const advRes = await axios.get('/financial-transactions/type/ADVANCE');
      const advData = advRes.data.data || advRes.data || [];
      setAdvances(Array.isArray(advData) ? advData : []);

      // Fetch settlement expenses (referenceNo = ADV-{id})
      const expRes = await axios.get('/financial-transactions/type/EXPENSE');
      const expData = expRes.data.data || expRes.data || [];
      const settledExpenses = (Array.isArray(expData) ? expData : [])
        .filter(t => t.referenceNo && t.referenceNo.match(/^ADV-\d+$/));

      setSettlements(settledExpenses.map(t => t.referenceNo));

      // Build settlement map from EXPENSE records (ADV-{id})
      const map = {};
      settledExpenses.forEach(t => {
        const advId = t.referenceNo.replace('ADV-', '');
        map[advId] = (map[advId] || 0) + parseFloat(t.amount || 0);
      });
      setSettlementMap(map);

      // Build return map from INCOME records (ADV-{id}-RETURN)
      const incomeRes = await axios.get('/financial-transactions/type/INCOME');
      const incomeData = incomeRes.data.data || incomeRes.data || [];
      const returnedIncomes = (Array.isArray(incomeData) ? incomeData : [])
        .filter(t => t.referenceNo && t.referenceNo.match(/^ADV-\d+-RETURN$/));
      const rMap = {};
      returnedIncomes.forEach(t => {
        const advId = t.referenceNo.replace('ADV-', '').replace('-RETURN', '');
        rMap[advId] = (rMap[advId] || 0) + parseFloat(t.amount || 0);
      });
      setReturnMap(rMap);
    } catch (e) { console.error(e); }
    finally { setHistLoading(false); }
  }, []);

  const flash = (type, text) => {
    if (type === 'error') {
      toast.error(text);
    } else {
      toast.success(text);
    }
  };

  // ── Give Cash ──────────────────────────────────────────────────────────────
  const handleGive = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post('/financial-transactions', {
        transactionType: 'ADVANCE',
        transactionDate: giveForm.date,
        employeeId: parseInt(giveForm.employeeId),
        amount: String(giveForm.amount),
        paymentAccountHeadId: giveForm.paymentAccountHeadId ? parseInt(giveForm.paymentAccountHeadId) : null,
        notes: giveForm.notes || 'Cash advance to supervisor',
      });
      flash('success', 'Cash advance recorded successfully');
      setGiveForm({ employeeId: '', amount: '', paymentAccountHeadId: '', date: new Date().toISOString().split('T')[0], notes: '' });
      fetchAdvances();
    } catch (err) {
      flash('error', err.response?.data?.message || 'Failed to record advance');
    } finally { setLoading(false); }
  };

  // ── Settle ─────────────────────────────────────────────────────────────────
  const openSettle = (adv) => {
    setSelectedAdvance(adv);
    const balance = getBalance(adv);
    setSettleForm({
      advanceId: adv.id,
      settledAmount: String(balance.toFixed(2)),
      returnedAmount: '0',
      date: new Date().toISOString().split('T')[0],
      notes: '',
    });
    setTab('settle');
  };

  const handleSettle = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const spent    = parseFloat(settleForm.settledAmount)  || 0;
      const returned = parseFloat(settleForm.returnedAmount) || 0;
      const balance  = getBalance(selectedAdvance);

      if (spent + returned > balance + 0.01) {
        flash('error', `Spent (${formatCurrency(spent)}) + Returned (${formatCurrency(returned)}) cannot exceed balance (${formatCurrency(balance)})`);
        setLoading(false);
        return;
      }
      if (spent <= 0 && returned <= 0) {
        flash('error', 'Enter amount spent or cash returned');
        setLoading(false);
        return;
      }

      // Record expense for the spent portion
      if (spent > 0) {
        await axios.post('/financial-transactions', {
          transactionType: 'EXPENSE',
          transactionDate: settleForm.date,
          employeeId: selectedAdvance?.employeeId || null,
          amount: String(spent),
          referenceNo: `ADV-${settleForm.advanceId}`,
          notes: `Settlement of advance ADV-${settleForm.advanceId}. ${settleForm.notes || ''}`.trim(),
        });
      }

      // If cash returned, record as INCOME back to the cash account
      if (returned > 0) {
        await axios.post('/financial-transactions', {
          transactionType: 'INCOME',
          transactionDate: settleForm.date,
          employeeId: selectedAdvance?.employeeId || null,
          amount: String(returned),
          referenceNo: `ADV-${settleForm.advanceId}-RETURN`,
          paymentAccountHeadId: selectedAdvance?.paymentAccountHeadId || null,
          notes: `Cash returned from advance ADV-${settleForm.advanceId}`,
        });
      }

      flash('success', `Settled — ${formatCurrency(spent)} spent, ${formatCurrency(returned)} returned`);
      setSelectedAdvance(null);
      setTab('history');
      fetchAdvances();
    } catch (err) {
      flash('error', err.response?.data?.message || 'Failed to record settlement');
    } finally { setLoading(false); }
  };

  // Balance = advance - spent - returned (both reduce the outstanding)
  const getBalance = (adv) => {
    const spent    = settlementMap[String(adv.id)] || 0;
    const returned = returnMap[String(adv.id)]     || 0;
    return parseFloat(adv.amount || 0) - spent - returned;
  };

  // Unsettled = advance has balance > 0 (includes partial settlements)
  const unsettled = advances.filter(adv => getBalance(adv) > 0.005);

  return (
    <div className="cash-advance-page">
      <div className="page-header">
        <div>
          <h1>Cash Advances</h1>
          <p>Give cash to supervisors and track settlements</p>
        </div>
      </div>

      {/* Summary bar */}
      <div className="ca-summary">
        <div className="ca-stat">
          <span className="ca-stat-val">{advances.filter(a => a.transactionType === 'ADVANCE').length}</span>
          <span className="ca-stat-lbl">Total Advances</span>
        </div>
        <div className="ca-stat ca-pending">
          <span className="ca-stat-val">{unsettled.length}</span>
          <span className="ca-stat-lbl">Unsettled</span>
        </div>
        <div className="ca-stat">
          <span className="ca-stat-val">
            {formatCurrency(unsettled.reduce((s, a) => s + getBalance(a), 0))}
          </span>
          <span className="ca-stat-lbl">Total Outstanding</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="ca-tabs">
        {[
          { key: 'give',    label: '💵 Give Cash' },
          { key: 'settle',  label: '✅ Settle',   disabled: !selectedAdvance },
          { key: 'history', label: '📋 History' },
        ].map(t => (
          <button key={t.key}
            className={`ca-tab ${tab === t.key ? 'ca-tab-active' : ''}`}
            onClick={() => !t.disabled && setTab(t.key)}
            disabled={t.disabled}
          >{t.label}</button>
        ))}
      </div>

      {/* ── Give Cash Tab ── */}
      {tab === 'give' && (
        <div className="ca-panel">
          <form onSubmit={handleGive} className="ca-form">
            <div className="form-row">
              <div className="form-group">
                <label>Supervisor / Employee *</label>
                <select value={giveForm.employeeId}
                  onChange={e => setGiveForm(p => ({ ...p, employeeId: e.target.value }))} required>
                  <option value="">Select Employee</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.userName || emp.name} {emp.employeeCode ? `(${emp.employeeCode})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Date *</label>
                <input type="date" value={giveForm.date}
                  onChange={e => setGiveForm(p => ({ ...p, date: e.target.value }))} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Amount *</label>
                <input type="number" value={giveForm.amount}
                  onChange={e => setGiveForm(p => ({ ...p, amount: e.target.value }))}
                  placeholder="0.00" step="0.01" min="1" required />
              </div>
              <div className="form-group">
                <label>Pay From Account</label>
                <select value={giveForm.paymentAccountHeadId}
                  onChange={e => setGiveForm(p => ({ ...p, paymentAccountHeadId: e.target.value }))}>
                  <option value="">Select Account (optional)</option>
                  {paymentAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Notes / Purpose</label>
              <textarea rows={2} value={giveForm.notes}
                onChange={e => setGiveForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="e.g. Site materials, labour payment for Block A…" />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Recording…' : '💵 Give Cash'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Settle Tab ── */}
      {tab === 'settle' && selectedAdvance && (
        <div className="ca-panel">
          <div className="ca-advance-info">
            <span>Advance to <strong>{selectedAdvance.employeeName || '—'}</strong></span>
            <span>Given: <strong>{formatCurrency(selectedAdvance.amount)}</strong></span>
            {(settlementMap[String(selectedAdvance.id)] || 0) > 0 && (
              <span>Spent so far: <strong style={{ color: '#dc3545' }}>{formatCurrency(settlementMap[String(selectedAdvance.id)])}</strong></span>
            )}
            {(returnMap[String(selectedAdvance.id)] || 0) > 0 && (
              <span>Returned so far: <strong style={{ color: '#28a745' }}>{formatCurrency(returnMap[String(selectedAdvance.id)])}</strong></span>
            )}
            <span>Remaining: <strong style={{ color: '#dc3545' }}>{formatCurrency(getBalance(selectedAdvance))}</strong></span>
            <span>Date: {formatDate(selectedAdvance.transactionDate)}</span>
            {selectedAdvance.notes && <span>Note: {selectedAdvance.notes}</span>}
          </div>
          <form onSubmit={handleSettle} className="ca-form">
            <div className="form-row">
              <div className="form-group">
                <label>Amount Spent *</label>
                <input type="number" value={settleForm.settledAmount}
                  onChange={e => setSettleForm(p => ({ ...p, settledAmount: e.target.value }))}
                  step="0.01" min="0" required />
                <small>How much was actually spent</small>
              </div>
              <div className="form-group">
                <label>Cash Returned</label>
                <input type="number" value={settleForm.returnedAmount}
                  onChange={e => setSettleForm(p => ({ ...p, returnedAmount: e.target.value }))}
                  step="0.01" min="0" placeholder="0.00" />
                <small>Cash given back to office</small>
              </div>
            </div>
            <div className="ca-settlement-calc">
              Balance: <strong>{formatCurrency(getBalance(selectedAdvance))}</strong>
              &nbsp;→&nbsp;
              Spent: <strong>{formatCurrency(parseFloat(settleForm.settledAmount || 0))}</strong>
              &nbsp;+&nbsp;
              Returned: <strong>{formatCurrency(parseFloat(settleForm.returnedAmount || 0))}</strong>
              &nbsp;=&nbsp;
              <strong style={{
                color: Math.abs(parseFloat(settleForm.settledAmount || 0) + parseFloat(settleForm.returnedAmount || 0) - getBalance(selectedAdvance)) < 0.01
                  ? '#28a745' : '#dc3545'
              }}>
                {formatCurrency(parseFloat(settleForm.settledAmount || 0) + parseFloat(settleForm.returnedAmount || 0))}
              </strong>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Settlement Date *</label>
                <input type="date" value={settleForm.date}
                  onChange={e => setSettleForm(p => ({ ...p, date: e.target.value }))} required />
              </div>
            </div>
            <div className="form-group">
              <label>Settlement Notes *</label>
              <textarea rows={3} value={settleForm.notes}
                onChange={e => setSettleForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="List expenses — materials, labour, etc." required />
            </div>
            <div className="form-actions">
              <button type="button" className="btn-secondary"
                onClick={() => { setSelectedAdvance(null); setTab('history'); }}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Saving…' : '✅ Record Settlement'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── History Tab ── */}
      {tab === 'history' && (
        <div className="ca-panel">
          {histLoading ? (
            <div className="loading">Loading…</div>
          ) : advances.filter(a => a.transactionType === 'ADVANCE').length === 0 ? (
            <div className="no-data-panel">No advances recorded yet.</div>
          ) : (
            <>
              {unsettled.length > 0 && (
                <>
                  <h3 className="ca-section-title">⏳ Unsettled Advances</h3>
                  <div className="ca-list">
                    {unsettled.map(adv => (
                      <div key={adv.id} className="ca-item ca-item-pending">
                        <div className="ca-item-top">
                          <span className="ca-employee">{adv.employeeName || '—'}</span>
                          <span className="ca-amount">{formatCurrency(getBalance(adv))} remaining</span>
                        </div>
                        <div className="ca-item-mid">
                          <span className="ca-date">{formatDate(adv.transactionDate)}</span>
                          <span>Given: {formatCurrency(adv.amount)}</span>
                          {(settlementMap[String(adv.id)] || 0) > 0 && (
                            <span style={{ color: '#dc3545' }}>Spent: {formatCurrency(settlementMap[String(adv.id)])}</span>
                          )}
                          {(returnMap[String(adv.id)] || 0) > 0 && (
                            <span style={{ color: '#28a745' }}>Returned: {formatCurrency(returnMap[String(adv.id)])}</span>
                          )}
                          {adv.notes && <span className="ca-notes">{adv.notes}</span>}
                        </div>
                        <div className="ca-item-bot">
                          <span className="ca-voucher">{adv.voucherNo}</span>
                          <button className="btn-primary ca-settle-btn" onClick={() => openSettle(adv)}>
                            Settle
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <h3 className="ca-section-title" style={{ marginTop: unsettled.length ? 24 : 0 }}>
                📋 All Advances
              </h3>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Voucher</th>
                      <th>Date</th>
                      <th>Employee</th>
                      <th>Notes</th>
                      <th className="text-right">Given</th>
                      <th className="text-right">Spent</th>
                      <th className="text-right">Returned</th>
                      <th className="text-right">Balance</th>
                      <th>Approval</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {advances.filter(a => a.transactionType === 'ADVANCE').map(adv => {
                      const spent    = settlementMap[String(adv.id)] || 0;
                      const returned = returnMap[String(adv.id)]     || 0;
                      const balance  = getBalance(adv);
                      const isFullySettled = balance <= 0.005;
                      const isPartial = (spent + returned) > 0 && !isFullySettled;
                      return (
                        <tr key={adv.id}>
                          <td className="voucher-no">{adv.voucherNo}</td>
                          <td>{formatDate(adv.transactionDate)}</td>
                          <td>{adv.employeeName || '—'}</td>
                          <td>{adv.notes || '—'}</td>
                          <td className="amount text-right">{formatCurrency(adv.amount)}</td>
                          <td className="amount text-right">{formatCurrency(spent)}</td>
                          <td className="amount text-right">{formatCurrency(returned)}</td>
                          <td className="amount text-right" style={{ color: isFullySettled ? '#28a745' : '#dc3545' }}>
                            {formatCurrency(balance)}
                          </td>
                          <td>{renderStatus(adv)}</td>
                          <td>
                            {adv.status !== 'APPROVED'
                              ? <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Awaiting Approval</span>
                              : isFullySettled
                              ? <span className="badge" style={{ background: '#d4edda', color: '#155724' }}>Settled</span>
                              : isPartial
                              ? <button className="recent-edit-btn" style={{ color: '#856404', background: '#fff3cd', border: '1px solid #ffc107' }} onClick={() => openSettle(adv)}>Partial — Settle More</button>
                              : <button className="recent-edit-btn" onClick={() => openSettle(adv)}>Settle</button>
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
      {renderHistoryModal()}
    </div>
  );
}

export default CashAdvance;
